import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { labelsByStageState, StageState } from '../../../shared/models/enums/stage-state.enum';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { StageNatureTypeService } from '../../../shared/services/stageNatureType.service';
import { StagesService } from '../../../shared/services/stages.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { Utils } from '../../../shared/utils';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { DisplayStageNatureTypeDto } from './display-stage-nature-type.dto';
import { DisplayStage } from './displayStage';
import { StageDetailsComponent } from './stage-details/stage-details.component';

@Component({
  selector: 'app-stage',
  templateUrl: './stage.component.html',
  standalone: false,
})
export class StageComponent implements OnInit, OnDestroy {
  $stageState = Utils.castTo<StageState>();

  public beneficiary: BeneficiaryDto;
  private static readonly resetStages: Pagination<DisplayStage> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));
  public readonly stageStates: Map<StageState, string> = labelsByStageState;

  public stageNaturesTypesById: Map<number, DisplayStageNatureTypeDto> = new Map<number, DisplayStageNatureTypeDto>();
  public id: number | undefined;
  public user: UserDto | undefined = undefined;

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public stages: Pagination<DisplayStage> = StageComponent.resetStages;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private readonly stageService: StagesService,
    private readonly stageNatureType: StageNatureTypeService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly beneficiariesService: BeneficiariesService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();

    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));
    this.stageNaturesTypesById = await this.stageNatureType.findAllNaturesTypesById();

    await this.updateStages(this.stages.options);

    this.stageService.stageCreated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateStages(this.stages.options);
    });
    this.stageService.stageUpdate$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateStages(this.stages.options);
    });
    this.stageService.stageDeleted$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateStages(this.stages.options);
    });
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateStages(this.stages.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateStages(this.stages.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event?.target?.value) {
      const limit: number = event.target.value;
      await this.updateStages(this.stages.options.withLimit(limit));
    }
  }

  public async openCreateModal(stageId?: number): Promise<void> {
    if (this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group))) {
      this.openModal(stageId);
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async openConsultModal(stageId?: number): Promise<void> {
    if (this.user?.groups?.some((group: string) => ['ADMIN', 'TERRITORY', 'DIRECTION', 'DIRECTION+', 'STRUCTURE'].includes(group))) {
      try {
        await this.stageService.findByStageId(stageId);
        this.openModal(stageId);
      } catch (error) {
        if (error.status === 437) {
          this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  private async openModal(stageId?: number): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(StageDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: StageDetailsComponent = modal.componentInstance as StageDetailsComponent;
    await modalComponent.onInit(this.beneficiary.id, stageId);
  }

  private async updateStages(pagination: PaginationOptions): Promise<void> {
    this.stages = await this.stageService.findByBeneficiaryId(this.beneficiary.id, pagination);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
